import { Lead } from '@/pages/Novi/types/Leads';
import apiClient from '@/utils/apiClient';

const NOVI_LEAD = '/api/novi/leads';
const ADD_LEAD_VIA_CSV = '/api/novi/leads/csv';
const UPDATE_LEAD_STATUS = '/api/novi/leads/:lead_id/status';
const UPDATE_LEAD_DETAILS = '/api/novi/leads/:lead_id';
const DELETE_LEAD = '/api/novi/leads/:lead_id';
const CALL_A_LEAD = '/api/novi/call';
const BULK_UPDATE_LEAD_STATUS = '/api/novi/leads/bulk-update';
const BULK_DELETE_LEADS = '/api/novi/leads/bulk-delete';

const NOVI = 'NOVI';

export const addNewLead = async (payload: {
	campaign_id?: string;
	lead: {
		phone_number: string;
		variables: Record<string, string>;
	};
}): Promise<{ message: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: NOVI_LEAD,
		clientType: NOVI,
		body: payload,
	});

	return response;
};

export const addLeadsViaCSV = async (payload: {
	campaign_id: string;
	file: File;
}): Promise<{ message: string }> => {
	const formData = new FormData();
	formData.append('csv', payload.file);

	const response = await apiClient.request({
		method: 'POST',
		endPoint: `${ADD_LEAD_VIA_CSV}?campaign_id=${payload.campaign_id}`,
		clientType: NOVI,
		body: formData,
	});

	return response;
};

export const getLeads = async (
	campaign_id: string,
	query: {
		status?: string;
		nextKey?: string;
		limit: number;
		phoneNumber?: string;
	},
): Promise<{
	leads: Lead[];
	pagination: {
		nextKey: string | null;
		hasMore: boolean;
	};
	leadStats?: {
		total: string;
		daily: string;
		totalRemaining: number;
		dailyRemaining: number;
	};
}> => {
	const params = new URLSearchParams({
		campaignId: campaign_id,
		limit: query.limit.toString(),
	});

	if (query.status) {
		params.append('leadStatus', query.status);
	}
	if (query.nextKey) {
		params.append('nextKey', query.nextKey);
	}
	if (query.phoneNumber) {
		params.append('phoneNumber', query.phoneNumber);
	}

	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${NOVI_LEAD}?${params.toString()}`,
		clientType: NOVI,
	});

	return {
		...response,
		pagination: {
			...response.pagination,
			nextKey: response.pagination?.nextKey || null,
		},
	};
};

export const getLeadCountsByStatus = async (
	campaign_id: string,
): Promise<{
	campaignId: string;
	statusCounts: Record<string, number>;
}> => {
	const params = new URLSearchParams({
		campaignId: campaign_id,
	});

	const response = await apiClient.request({
		method: 'GET',
		endPoint: `${NOVI_LEAD}/status-counts?${params.toString()}`,
		clientType: NOVI,
	});

	return response;
};

export const getPollingLeads = async (
	campaign_id: string,
	lastUpdatedAt: number,
	query: {
		status: string;
		nextKey: string;
		limit: number;
	},
): Promise<{
	leads: Lead[];
	pagination: {
		nextKey: string | null;
		hasMore: boolean;
	};
}> => {
	const params = new URLSearchParams({
		campaignId: campaign_id,
		leadStatus: query.status,
		limit: query.limit.toString(),
		lastUpdatedAt: lastUpdatedAt.toString(),
	});

	if (query.nextKey) {
		params.append('nextKey', query.nextKey);
	}

	const response = await apiClient.request({
		method: 'GET',
		endPoint: NOVI_LEAD + '/polling?' + params.toString(),
		clientType: NOVI,
	});

	return {
		...response,
		pagination: {
			...response.pagination,
			nextKey: response.pagination.nextKey || null,
		},
	};
};

export const updateLeadStatus = async (payload: {
	campaign_id: string;
	lead_id: string;
	status: string;
}): Promise<{ message: string }> => {
	const response = await apiClient.request({
		method: 'PATCH',
		endPoint: UPDATE_LEAD_STATUS.replace(':lead_id', payload.lead_id),
		clientType: NOVI,
		body: payload,
	});

	return response;
};

export const bulkLeadsStatusUpdate = async (payload: {
	campaign_id: string;
	lead_ids: string[];
	status: string;
}): Promise<{
	message: string;
	results: { total: number; successful: number; failed: number };
}> => {
	const response = await apiClient.request({
		method: 'PATCH',
		endPoint: BULK_UPDATE_LEAD_STATUS,
		clientType: NOVI,
		body: payload,
	});

	return response;
};

export const updateLeadDetails = async (payload: {
	campaign_id: string;
	lead_id: string;
	leadDetails: {
		variables: Record<string, string>;
		status: string;
	};
}): Promise<{ message: string }> => {
	const response = await apiClient.request({
		method: 'PATCH',
		endPoint: UPDATE_LEAD_DETAILS.replace(':lead_id', payload.lead_id),
		clientType: NOVI,
		body: payload,
	});

	return response;
};

export const deleteLead = async (payload: {
	campaign_id: string;
	lead_id: string;
}): Promise<{ message: string }> => {
	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: DELETE_LEAD.replace(':lead_id', payload.lead_id),
		clientType: NOVI,
		body: { campaign_id: payload.campaign_id },
	});

	return response;
};

export const bulkLeadsDelete = async (payload: {
	campaign_id: string;
	lead_ids: string[];
}): Promise<{
	message: string;
	results: { total: number; successful: number; failed: number };
}> => {
	const response = await apiClient.request({
		method: 'DELETE',
		endPoint: BULK_DELETE_LEADS,
		clientType: NOVI,
		body: payload,
	});

	return response;
};

export const makeACall = async (payload: {
	campaign_id: string;
	lead_id: string;
}): Promise<{ message: string }> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: CALL_A_LEAD,
		clientType: NOVI,
		body: payload,
	});

	return response;
};
